/** Week grid helpers (Monday-first, Europe/Lisbon display). */

export const CALENDAR_HOUR_START = 8;
export const CALENDAR_HOUR_END = 20;
export const CALENDAR_SLOT_MINUTES = 30;

const WEEKDAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

export function startOfWeekMonday(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function weekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

export function formatWeekdayLabel(d: Date): string {
  const idx = d.getDay() === 0 ? 6 : d.getDay() - 1;
  return WEEKDAY_SHORT[idx];
}

export function formatDayHeader(d: Date): string {
  return `${formatWeekdayLabel(d)} ${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
    .toString()
    .padStart(2, '0')}`;
}

export function formatWeekRangeLabel(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const fmt = (x: Date) =>
    `${x.getDate().toString().padStart(2, '0')}/${(x.getMonth() + 1).toString().padStart(2, '0')}/${x.getFullYear()}`;
  return `${fmt(weekStart)} – ${fmt(end)}`;
}

export function parseApiDate(iso: string): Date {
  return new Date(iso.replace('Z', ''));
}

export function toApiIso(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:00Z`;
}

export function weekRangeIso(weekStart: Date): { from: string; to: string } {
  const start = new Date(weekStart);
  start.setHours(0, 0, 0, 0);
  const end = addDays(start, 7);
  end.setHours(0, 0, 0, 0);
  return { from: toApiIso(start), to: toApiIso(end) };
}

/** Minutes from CALENDAR_HOUR_START for positioning in day column. */
export function minutesFromGridStart(d: Date): number {
  return d.getHours() * 60 + d.getMinutes() - CALENDAR_HOUR_START * 60;
}

export function gridTotalMinutes(): number {
  return (CALENDAR_HOUR_END - CALENDAR_HOUR_START) * 60;
}

export function eventBlockStyle(start: Date, end: Date): { top: string; height: string } {
  const total = gridTotalMinutes();
  const topMin = Math.max(0, minutesFromGridStart(start));
  const endMin = Math.min(total, minutesFromGridStart(end));
  const heightMin = Math.max(18, endMin - topMin);
  return {
    top: `${(topMin / total) * 100}%`,
    height: `${(heightMin / total) * 100}%`,
  };
}

export type TimedLayoutInput = {
  id: string | number;
  start: Date;
  end: Date;
};

export type EventBlockLayout = {
  top: string;
  height: string;
  left: string;
  width: string;
};

function eventsOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime();
}

/** Side-by-side columns (+ optional width expansion) for overlapping events in one day. */
export function layoutEventsInDay(items: TimedLayoutInput[]): Map<string | number, EventBlockLayout> {
  const result = new Map<string | number, EventBlockLayout>();
  if (items.length === 0) return result;

  if (items.length === 1) {
    const ev = items[0];
    result.set(ev.id, { ...eventBlockStyle(ev.start, ev.end), left: '0%', width: '100%' });
    return result;
  }

  const sorted = [...items].sort((a, b) => {
    const ds = a.start.getTime() - b.start.getTime();
    if (ds !== 0) return ds;
    return b.end.getTime() - b.start.getTime() - (a.end.getTime() - a.start.getTime());
  });

  const columns: TimedLayoutInput[][] = [];
  const colIndex = new Map<string | number, number>();

  for (const ev of sorted) {
    let assigned = -1;
    for (let c = 0; c < columns.length; c++) {
      const conflicts = columns[c].some((other) =>
        eventsOverlap(ev.start, ev.end, other.start, other.end)
      );
      if (!conflicts) {
        assigned = c;
        columns[c].push(ev);
        break;
      }
    }
    if (assigned === -1) {
      assigned = columns.length;
      columns.push([ev]);
    }
    colIndex.set(ev.id, assigned);
  }

  const columnCount = Math.max(1, columns.length);
  const gapPct = 1.5;
  const colUnit = (100 - gapPct * Math.max(0, columnCount - 1)) / columnCount;

  for (const ev of sorted) {
    const col = colIndex.get(ev.id) ?? 0;
    let span = 1;
    for (let c = col + 1; c < columnCount; c++) {
      const blocked = columns[c].some((other) =>
        eventsOverlap(ev.start, ev.end, other.start, other.end)
      );
      if (blocked) break;
      span += 1;
    }
    const left = col * (colUnit + gapPct);
    const width = span * colUnit + gapPct * Math.max(0, span - 1);
    result.set(ev.id, {
      ...eventBlockStyle(ev.start, ev.end),
      left: `${left}%`,
      width: `${width}%`,
    });
  }

  return result;
}

export function slotDate(weekStart: Date, dayIndex: number, hour: number, minute: number): Date {
  const d = addDays(weekStart, dayIndex);
  d.setHours(hour, minute, 0, 0);
  return d;
}

export function formatTimeLabel(d: Date): string {
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

export function fromDatetimeLocalValue(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
